import React, { useState } from 'react';
import { PrimaryButton, Card, ScreenHeader, Pill } from '../ui';
import gamesService from '../../../services/gamesService';
import { useGameEligibility } from '../useGameEligibility';
import GameLockedCard from '../GameLockedCard';

interface MysteryBoxGameProps {
  appName: string;
  outletId: number;
  onClose?: () => void;
}

const MysteryBoxGame: React.FC<MysteryBoxGameProps> = ({ appName, outletId, onClose }) => {
  const { loading, eligible, eligibility } = useGameEligibility(appName, 'MYSTERY_BOX');
  const [openedIndex, setOpenedIndex] = useState<number | null>(null);
  const [resultLabel, setResultLabel] = useState<string | null>(null);
  const [redemptionCode, setRedemptionCode] = useState<string | null>(null);
  const [loadingBox, setLoadingBox] = useState(false);
  const [played, setPlayed] = useState(false);

  async function openBox(i: number) {
    if (openedIndex !== null || loadingBox || !eligible || played) return;
    setLoadingBox(true);
    setOpenedIndex(i);
    try {
      const res = await gamesService.spin(
        appName,
        'MYSTERY_BOX',
        gamesService.dailyIdempotencyKey(outletId, 'MYSTERY_BOX')
      );
      setResultLabel(res.resultSegment);
      setRedemptionCode(res.resultSegment !== 'Try Again' ? res.redemptionCode : null);
      setPlayed(true);
    } catch {
      setResultLabel('Come back tomorrow for another try.');
      setPlayed(true);
    }
    setLoadingBox(false);
  }

  const locked = !loading && !eligible;

  return (
    <div style={{ background: 'var(--bg, #FFFFFF)', minHeight: '100vh', paddingBottom: 24 }}>
      <ScreenHeader
        title={<>Pick Your <span style={{ color: '#D9472B' }}>Mystery Box</span></>}
        subtitle="One box hides today's best reward"
        onClose={onClose}
      />
      <div style={{ padding: '18px 24px 0' }}>
        {loading && (
          <p style={{ textAlign: 'center', color: 'var(--text-muted, #9B8F6E)', marginTop: 40 }}>Loading…</p>
        )}

        {locked && <GameLockedCard eligibility={eligibility} />}

        {!loading && eligible && (
          <>
            <div style={{ display: 'flex', gap: 14, justifyContent: 'center', margin: '20px 0' }}>
              {[0, 1, 2].map((i) => (
                <button
                  key={i}
                  onClick={() => openBox(i)}
                  disabled={openedIndex !== null || played}
                  style={{
                    width: 78,
                    height: 78,
                    borderRadius: 14,
                    border: 'none',
                    cursor: openedIndex === null && !played ? 'pointer' : 'default',
                    background:
                      openedIndex === i
                        ? 'linear-gradient(160deg, var(--primary, #F5C21A), #D4A416)'
                        : 'linear-gradient(160deg, #FCEFCB, #F3DE9A)',
                    fontSize: 30,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(0,0,0,.10)',
                    opacity: openedIndex !== null && openedIndex !== i ? 0.45 : 1,
                    transition: 'all 200ms ease',
                  }}
                >
                  {openedIndex === i ? (loadingBox ? '⏳' : '🎉') : '🎁'}
                </button>
              ))}
            </div>

            {openedIndex === null && (
              <div style={{ textAlign: 'center' }}>
                <Pill>Choose 1 of 3</Pill>
              </div>
            )}

            {resultLabel && (
              <Card style={{ textAlign: 'center', marginTop: 8 }}>
                <div style={{ fontWeight: 800, fontSize: 19, color: 'var(--primary-dark, #D4A416)' }}>
                  {resultLabel}
                </div>
                {redemptionCode && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed var(--border-color, #E5E2D8)' }}>
                    <div style={{ fontSize: 10.5, color: 'var(--text-muted, #9B8F6E)' }}>CODE</div>
                    <div style={{ fontWeight: 700, fontSize: 17, letterSpacing: 2 }}>{redemptionCode}</div>
                  </div>
                )}
              </Card>
            )}

            <div style={{ height: 16 }} />
            <PrimaryButton disabled={!played || !redemptionCode}>
              {played ? 'COME BACK TOMORROW' : 'PICK A BOX'}
            </PrimaryButton>
            <p style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-muted, #9B8F6E)', marginTop: 10 }}>
              One pick per customer / day
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default MysteryBoxGame;
